import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Button,
  Card,
  EmptyState,
  Field,
  LoadingRegion,
  Notice,
  Status,
  VENUE_MAP_VERSION_STATES,
  presentation,
} from '@gatecheckpro/design-system';
import {
  venueMapCodeSchema,
  type Venue,
  type VenueMapDetail,
  type VenueMapSummary,
  type VenueMapVersionSummary,
} from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';

/**
 * The maps a venue has.
 *
 * A map is the shape of a room, and it belongs to the venue rather than to one
 * night at it (ADR 0036). That is the idea this page has to teach without a
 * manual: the same map is reused by every show held in the room, which is why
 * nothing here mentions an event, a ticket or a price.
 */
export function VenueMapsPage() {
  const { organizationId, venueId } = useParams<{ organizationId: string; venueId: string }>();
  const base = `/api/v1/organizer/organizations/${organizationId ?? ''}`;
  const venue = useResource<Venue>(`${base}/venues/${venueId ?? ''}`);
  // Collections come back enveloped, as everywhere else in this API — see
  // VenuesPage. Typing this as a bare array made `.length` undefined and the
  // first render throw, which with no error boundary anywhere above it took
  // the whole workspace to a blank page.
  const maps = useResource<{ maps: VenueMapSummary[] }>(`${base}/venues/${venueId ?? ''}/maps`);
  const command = useCommand<VenueMapDetail>();

  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');

  if (venue.resource.status === 'loading' || maps.resource.status === 'loading') {
    return <LoadingRegion label="Loading venue maps" />;
  }
  if (venue.resource.status === 'failed') return <FailureNotice error={venue.resource.error} />;
  if (maps.resource.status === 'failed') return <FailureNotice error={maps.resource.error} />;

  const codeProblem =
    code === '' || venueMapCodeSchema.safeParse(code).success
      ? undefined
      : 'Capitals, numbers and underscores, starting with a letter.';

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-stack gc-stack--tight">
        <span className="gc-eyebrow">
          <Link to={`/o/${organizationId ?? ''}/venues/${venueId ?? ''}`}>
            {venue.resource.data.name}
          </Link>
        </span>
        <h2>Maps</h2>
        <p className="gc-muted">
          The shape of a room — its sections, rows, seats and tables. A map belongs to the venue and
          is reused by every event held there.
        </p>
      </div>

      <FailureNotice error={command.error} />

      <Notice tone="info" title="A map says what the room is, not what is on sale">
        Drawing a room here does not put a seat on sale, set a price or admit anybody. What is sold
        in the room is configured against a map later, per event.
      </Notice>

      <VenueMapList organizationId={organizationId ?? ''} maps={maps.resource.data.maps} />

      <Card title="Add a map" eyebrow="One room">
        <form
          className="gc-stack"
          onSubmit={(formEvent) => {
            formEvent.preventDefault();
            void command
              .run(`${base}/venues/${venueId ?? ''}/maps`, {
                method: 'POST',
                body: { code, displayName },
              })
              .then((result) => {
                if (result === null) return;
                setCode('');
                setDisplayName('');
                maps.reload();
              });
          }}
        >
          <div className="gc-grid">
            <Field
              label="Code"
              placeholder="MAIN_HALL"
              required
              error={codeProblem}
              hint="Permanent. Later seat configuration references it."
              value={code}
              onChange={(input) => setCode(input.target.value.toUpperCase())}
            />
            <Field
              label="Name"
              placeholder="Main hall"
              required
              hint="What people read. Change it whenever."
              value={displayName}
              onChange={(input) => setDisplayName(input.target.value)}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            busy={command.busy}
            disabled={code === '' || displayName === '' || codeProblem !== undefined}
          >
            Add map
          </Button>
        </form>
      </Card>
    </div>
  );
}

/**
 * The maps themselves, separated from the fetch so the judgments in here can be
 * tested against a projection rather than against a network.
 */
export function VenueMapList({
  organizationId,
  maps,
}: {
  readonly organizationId: string;
  readonly maps: readonly VenueMapSummary[];
}) {
  return (
    <Card title="Maps at this venue" eyebrow={`${String(maps.length)} total`}>
      {maps.length === 0 ? (
        <EmptyState title="No maps yet">
          A venue needs a map before any of its seats can be reserved. Most venues need one per room
          — a main auditorium and a balcony are usually one map, not two.
        </EmptyState>
      ) : (
        <div className="gc-table-scroll">
          <table className="gc-table">
            <thead>
              <tr>
                <th scope="col">Code</th>
                <th scope="col">Name</th>
                <th scope="col">Approved version</th>
                <th scope="col">Open draft</th>
              </tr>
            </thead>
            <tbody>
              {maps.map((map) => (
                <tr key={map.venueMapId}>
                  <td className="gc-mono">
                    <Link to={`/o/${organizationId}/venue-maps/${map.venueMapId}`}>{map.code}</Link>
                  </td>
                  <td>
                    {map.displayName}
                    {map.status === 'RETIRED' && (
                      <>
                        {' '}
                        <Status tone="neutral" glyph="−" label="Retired" />
                      </>
                    )}
                  </td>
                  <td>
                    <VersionCell version={map.publishedVersion} absent="Never approved" />
                  </td>
                  <td>
                    <VersionCell version={map.draftVersion} absent="None open" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * One version, or an honest statement that there is not one.
 *
 * "None open" and "Never approved" are different facts from an empty cell, and
 * a reader should not have to infer which they are looking at.
 */
function VersionCell({
  version,
  absent,
}: {
  readonly version: VenueMapVersionSummary | null;
  readonly absent: string;
}) {
  if (version === null) return <span className="gc-muted">{absent}</span>;
  const state = presentation(VENUE_MAP_VERSION_STATES, version.lifecycle);
  return (
    <span className="gc-row">
      <Status tone={state.tone} glyph={state.glyph} label={state.label} title={state.meaning} />
      <span className="gc-muted gc-mono">v{version.versionNumber}</span>
    </span>
  );
}
